import React, { useState, useRef, useEffect } from 'react';
import { announcement } from '../mock';
import { User, ChevronDown, X, Menu, LogOut, BookOpen, Newspaper, Users, Settings, GraduationCap, FolderOpen, Mail, BadgeCheck, Calculator, HelpCircle, BookText, Info, Globe, PlayCircle, Sparkles, Compass, Clapperboard, Film, TrendingUp, MessageSquare, ClipboardCheck } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Logo from './Logo';

// ── Nav desktop épurée (style YouTube/TikTok) : 4 pills max + menu Explorer ──
const PILLS_VISITOR = [
  { label: 'Shorts', to: '/shorts', icon: Clapperboard, highlight: true, tid: 'header-nav-shorts' },
  { label: 'Cours', to: '/cours-interactifs', icon: BookOpen, tid: 'header-nav-cours' },
  { label: 'Vidéos', to: '/videos', icon: Film, tid: 'header-nav-videos' },
  { label: 'Actu', to: '/magazine', icon: Newspaper, tid: 'header-nav-actu' },
];

const PILLS_ELEVE = [
  { label: 'Mes cours', to: '/cours/eleve/voiture', icon: GraduationCap, highlight: true, tid: 'header-nav-eleve-cours' },
  { label: 'Progression', to: '/cours/progression', icon: TrendingUp, tid: 'header-nav-eleve-progression' },
  { label: 'Shorts', to: '/shorts', icon: Clapperboard, tid: 'header-nav-eleve-shorts' },
  { label: 'Actu', to: '/magazine', icon: Newspaper, tid: 'header-nav-eleve-actu' },
];

const PILLS_ENSEIGNANT = [
  { label: 'Élèves', to: '/enseignant/dashboard', icon: Users, highlight: true, tid: 'header-nav-ens-eleves' },
  { label: 'Mes cours', to: '/enseignant/mes-cours', icon: BookOpen, tid: 'header-nav-ens-cours' },
  { label: 'Salle & RDV', to: '/enseignant/seances', icon: ClipboardCheck, tid: 'header-nav-ens-salle' },
  { label: 'Shorts', to: '/shorts', icon: Clapperboard, tid: 'header-nav-ens-shorts' },
];

// Menu Explorer : tout le reste, organisé en 3 colonnes
const EXPLORE_SECTIONS = [
  {
    category: 'Se former',
    items: [
      { label: 'Espace Élève', to: '/eleve', icon: GraduationCap, desc: 'Permis B · AAC · CS · B96' },
      { label: 'Mon premier départ', to: '/mon-premier-depart', icon: BadgeCheck, desc: 'Livret dès 14 ans · deux-roues & voiturette' },
      { label: 'Espace Enseignant', to: '/enseignant', icon: Users, desc: 'Moniteur ECSR · suivi élèves' },
      { label: 'Parcours & pré-requis', to: '/formation/parcours', icon: BadgeCheck, desc: 'Classique · AAC · RDV obligatoires' },
      { label: 'Tutos vidéo', to: '/tutos', icon: PlayCircle, desc: 'Manœuvres et techniques' },
      { label: 'Ma progression', to: '/cours/progression', icon: TrendingUp, desc: '38 leçons REMC' },
    ],
  },
  {
    category: 'Outils interactifs',
    items: [
      { label: 'Modules de conduite', to: '/modules-conduite', icon: Sparkles, desc: 'SVG interactifs · simulateurs' },
      { label: 'Simulateurs', to: '/simulateurs', icon: Calculator, desc: 'Coût · Points · Distance d\'arrêt' },
      { label: 'Vidéos communauté', to: '/videos', icon: Film, desc: 'Galerie validée par l\'équipe' },
      { label: 'Livret AM', to: '/livret-am/lecteur', icon: BookText, desc: 'Formation AM/BSR' },
    ],
  },
  {
    category: 'Ressources',
    items: [
      { label: 'Base documentaire', to: '/ressources', icon: FolderOpen, desc: 'Documents officiels' },
      { label: 'Annuaire écoles', to: '/annuaire', icon: Globe, desc: 'Auto-écoles de France' },
      { label: 'Glossaire ECSR', to: '/glossaire', icon: BookText, desc: '30+ sigles et définitions' },
      { label: 'FAQ', to: '/faq', icon: HelpCircle, desc: 'Questions courantes' },
      { label: 'À propos', to: '/a-propos', icon: Info, desc: 'Notre mission · nos valeurs' },
      { label: 'Nous contacter', to: '/contact', icon: Mail, desc: 'Formulaire direct' },
    ],
  },
];

export default function Header({ hideBanners = false }) {
  const [showAlert, setShowAlert] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);
  const exploreRef = useRef(null);
  const exploreBtnRef = useRef(null);
  const { user, openLogin, openRegister, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isHomePublic = pathname === '/';

  const PILLS = user?.role === 'enseignant' ? PILLS_ENSEIGNANT
              : user?.role === 'eleve' ? PILLS_ELEVE
              : PILLS_VISITOR;

  const onLogout = () => {
    setUserMenu(false);
    logout();
    navigate('/');
  };

  useEffect(() => {
    if (!exploreOpen) return;
    const onClick = (e) => {
      if (exploreRef.current && !exploreRef.current.contains(e.target)
        && exploreBtnRef.current && !exploreBtnRef.current.contains(e.target)) setExploreOpen(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setExploreOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onEsc); };
  }, [exploreOpen]);

  // Fermer l'Explorer à chaque navigation
  useEffect(() => { setExploreOpen(false); setUserMenu(false); }, [pathname]);

  const isActive = (to) => pathname === to || (to !== '/' && pathname.startsWith(to + '/'));

  return (
    <header className="relative z-40">
      {/* Nav */}
      <div className="relative z-50 bg-[#0a0a0b]/85 backdrop-blur-xl border-b border-white/10 sticky top-0">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 h-[68px] flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2.5 group shrink min-w-0">
            <span className="hidden sm:block"><Logo variant="horizontal" height={52} /></span>
            <span className="sm:hidden"><Logo variant="horizontal" height={38} /></span>
          </Link>

          {/* Desktop nav — 4 pills + Explorer */}
          <nav aria-label="Navigation principale" className="hidden lg:flex items-center gap-1.5" data-testid="header-desktop-nav">
            {PILLS.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.to);
              return (
                <Link key={item.label} to={item.to} data-testid={item.tid}
                  className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-[13px] font-semibold transition-colors ${
                    active ? 'bg-red-600 text-white shadow-md shadow-red-900/30'
                    : item.highlight ? 'bg-white/[0.06] border border-red-500/30 text-white hover:bg-red-600/20'
                    : 'text-white/70 hover:text-white hover:bg-white/[0.07]'
                  }`}>
                  <Icon size={14} className={active ? 'text-white' : item.highlight ? 'text-red-400' : ''} />
                  {item.label}
                </Link>
              );
            })}
            <button ref={exploreBtnRef} data-testid="header-explore-btn" onClick={() => setExploreOpen((v) => !v)} aria-expanded={exploreOpen} aria-haspopup="menu"
              className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-[13px] font-semibold transition-colors ${exploreOpen ? 'bg-white/10 text-white' : 'text-white/70 hover:text-white hover:bg-white/[0.07]'}`}>
              <Compass size={14} /> Explorer <ChevronDown size={13} className={`transition-transform ${exploreOpen ? 'rotate-180' : ''}`} />
            </button>
          </nav>

          {/* Explorer : mega-menu 3 colonnes (style YouTube) */}
          {exploreOpen && (
            <div ref={exploreRef} data-testid="header-explore-menu"
              className="absolute top-[68px] left-1/2 -translate-x-1/2 mt-2 w-[min(880px,calc(100vw-32px))] rounded-2xl bg-[#111113] border border-white/10 shadow-2xl shadow-black/70 overflow-hidden z-50">
              <div className="grid grid-cols-3 divide-x divide-white/5">
                {EXPLORE_SECTIONS.map((sec) => (
                  <div key={sec.category} className="p-4">
                    <div className="px-2 pb-2 text-[10px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec">{sec.category}</div>
                    {sec.items.map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link key={item.to + item.label} to={item.to} onClick={() => setExploreOpen(false)}
                          data-testid={`explore-link-${item.to.replace(/[/#]/g, '-')}`}
                          className="flex items-start gap-2.5 px-2 py-2 rounded-xl text-white/85 hover:bg-white/[0.06] transition-colors">
                          <span className="w-7 h-7 rounded-lg bg-red-600/10 border border-red-700/25 flex items-center justify-center text-red-400 shrink-0 mt-0.5">
                            <Icon size={13} />
                          </span>
                          <span className="min-w-0">
                            <span className="block text-white text-[13px] font-semibold leading-tight">{item.label}</span>
                            <span className="block text-white/40 text-[11px] mt-0.5 leading-snug">{item.desc}</span>
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 shrink-0">
            {/* Auth mobile — style app */}
            {!user ? (
              <div className="flex md:hidden items-center gap-1">
                <button type="button" onClick={openLogin} data-testid="header-mobile-login" aria-label="Connexion"
                  className="w-8 h-8 rounded-full border border-white/15 bg-white/[0.04] text-white/85 flex items-center justify-center active:scale-90 transition shrink-0">
                  <User size={14} />
                </button>
                <button type="button" onClick={openRegister} data-testid="header-mobile-register"
                  className="h-8 px-2.5 rounded-full bg-gradient-to-r from-red-500 to-red-700 text-white text-[11px] font-bold flex items-center gap-1 shadow-md shadow-red-900/40 active:scale-95 transition whitespace-nowrap shrink-0">
                  <Sparkles size={11} /> Rejoindre
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => setMobileOpen(!mobileOpen)} data-testid="header-mobile-avatar" aria-label="Mon compte"
                className="md:hidden w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-red-700 ring-2 ring-red-500/40 text-white text-[12px] font-bold flex items-center justify-center active:scale-90 transition shrink-0">
                {user.name.charAt(0).toUpperCase()}
              </button>
            )}
            {user ? (
              <div className="relative">
                <button onClick={() => setUserMenu(!userMenu)} aria-expanded={userMenu} aria-haspopup="menu" aria-label="Menu utilisateur" data-testid="header-user-btn"
                  className="hidden md:flex items-center gap-2 px-2.5 h-9 rounded-full border border-white/15 bg-white/[0.03] hover:border-red-500/40 text-white/90 text-[13px] font-medium transition">
                  <span className="w-6 h-6 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center text-white text-[11px] font-bold">{user.name.charAt(0).toUpperCase()}</span>
                  {user.name.split(' ')[0]} <ChevronDown size={13} />
                </button>
                {userMenu && (
                  <div className="absolute right-0 mt-2 w-60 rounded-2xl bg-[#111113] border border-white/10 shadow-2xl shadow-black/50 overflow-hidden z-50">
                    <div className="px-4 py-3 border-b border-white/5">
                      <div className="text-white text-[13.5px] font-semibold">{user.name}</div>
                      <div className="text-white/50 text-[12px]">{user.email}</div>
                      <div className="text-[10px] uppercase tracking-wider text-red-400 mt-1 font-semibold">{user.is_admin ? 'admin' : user.role.replace('_', ' ')}</div>
                    </div>
                    {user.is_admin && (
                      <Link to="/admin" onClick={() => setUserMenu(false)} data-testid="header-admin-link" className="flex items-center gap-2 px-4 py-2.5 text-red-300 bg-red-950/25 hover:bg-red-950/40 font-semibold text-[13.5px]"><Settings size={14} /> Back-office admin</Link>
                    )}
                    {user.role !== 'enseignant' && !user.is_admin && (
                      <Link to="/livret" onClick={() => setUserMenu(false)} className="flex items-center gap-2 px-4 py-2.5 text-white/85 hover:bg-white/5 text-[13.5px]"><BookOpen size={14} /> Mon livret</Link>
                    )}
                    {user.role === 'eleve' && !user.is_admin && (
                      <Link to="/eleve/dashboard" onClick={() => setUserMenu(false)} className="flex items-center gap-2 px-4 py-2.5 text-white/85 hover:bg-white/5 text-[13.5px]"><GraduationCap size={14} /> Dashboard élève</Link>
                    )}
                    {user.role === 'enseignant' && (
                      <Link to="/enseignant/dashboard" onClick={() => setUserMenu(false)} className="flex items-center gap-2 px-4 py-2.5 text-white/85 hover:bg-white/5 text-[13.5px]"><Users size={14} /> Dashboard enseignant</Link>
                    )}
                    <Link to="/messages" onClick={() => setUserMenu(false)} className="flex items-center gap-2 px-4 py-2.5 text-white/85 hover:bg-white/5 text-[13.5px]"><MessageSquare size={14} /> Messages</Link>
                    <Link to="/?public=1" onClick={() => setUserMenu(false)} data-testid="header-public-home" className="flex items-center gap-2 px-4 py-2.5 text-white/65 hover:bg-white/5 text-[13.5px]"><Globe size={14} /> Page publique</Link>
                    <button onClick={onLogout} className="flex items-center gap-2 px-4 py-2.5 text-red-400 hover:bg-red-950/30 text-[13.5px] w-full text-left border-t border-white/5"><LogOut size={14} /> Se déconnecter</button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <button onClick={openLogin} data-testid="header-login-btn" className="hidden md:flex items-center gap-1.5 px-4 h-9 rounded-full border border-white/15 text-white/90 hover:border-white/40 hover:bg-white/5 text-[13px] font-medium transition-all">
                  <User size={14} /> Connexion
                </button>
                <button onClick={openRegister} data-testid="header-register-btn" className="hidden md:flex h-9 px-4 rounded-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white text-[13px] font-semibold shadow-md shadow-red-900/30 transition-all active:scale-95 items-center">
                  Créer un compte
                </button>
              </>
            )}
            <button onClick={() => setMobileOpen(!mobileOpen)} data-testid="header-mobile-toggle" className="lg:hidden w-9 h-9 flex items-center justify-center text-white shrink-0" aria-label={mobileOpen ? 'Fermer le menu' : 'Ouvrir le menu'} aria-expanded={mobileOpen}>
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile menu — version épurée : pas de duplication avec la bottom nav.
            Uniquement actions secondaires (Magazine, Tutos, etc.) + auth. */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-white/5 px-4 py-4 bg-[#0a0a0b]" data-testid="mobile-menu-drawer">
            {user && (
              <div className="flex items-center gap-3 pb-3 mb-3 border-b border-white/5">
                <span className="w-9 h-9 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center text-white text-[13px] font-bold">{user.name.charAt(0).toUpperCase()}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-white text-[14px] font-semibold truncate">{user.name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-red-400 font-semibold">{user.is_admin ? 'admin' : user.role.replace('_', ' ')}</div>
                </div>
              </div>
            )}
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-bold font-mono-spec mb-2">Raccourcis</div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <Link to="/magazine" onClick={() => setMobileOpen(false)} data-testid="mobile-menu-magazine" className="flex items-center gap-2 px-3 h-10 rounded-lg bg-white/[0.04] border border-white/10 text-white/85 text-[13px]"><Newspaper size={14} /> Magazine</Link>
              <Link to="/tutos" onClick={() => setMobileOpen(false)} data-testid="mobile-menu-tutos" className="flex items-center gap-2 px-3 h-10 rounded-lg bg-white/[0.04] border border-white/10 text-white/85 text-[13px]"><PlayCircle size={14} /> Tutos</Link>
              <Link to="/videos" onClick={() => setMobileOpen(false)} data-testid="mobile-menu-videos" className="flex items-center gap-2 px-3 h-10 rounded-lg bg-white/[0.04] border border-white/10 text-white/85 text-[13px]"><Film size={14} /> Vidéos</Link>
              <Link to="/faq" onClick={() => setMobileOpen(false)} data-testid="mobile-menu-faq" className="flex items-center gap-2 px-3 h-10 rounded-lg bg-white/[0.04] border border-white/10 text-white/85 text-[13px]"><HelpCircle size={14} /> FAQ</Link>
              <Link to="/annuaire" onClick={() => setMobileOpen(false)} data-testid="mobile-menu-annuaire" className="flex items-center gap-2 px-3 h-10 rounded-lg bg-white/[0.04] border border-white/10 text-white/85 text-[13px]"><FolderOpen size={14} /> Annuaire écoles</Link>
              <Link to="/livret-am/lecteur" onClick={() => setMobileOpen(false)} data-testid="mobile-menu-livret-am" className="flex items-center gap-2 px-3 h-10 rounded-lg bg-white/[0.04] border border-white/10 text-white/85 text-[13px]"><BookText size={14} /> Livret AM</Link>
              <Link to="/mon-premier-depart" onClick={() => setMobileOpen(false)} data-testid="mobile-menu-premier-depart" className="col-span-2 flex items-center justify-center gap-2 px-3 h-10 rounded-lg bg-orange-950/30 border border-orange-600/30 text-orange-200 text-[13px] font-semibold"><BookText size={14} /> Mon premier départ · dès 14 ans</Link>
              <Link to="/contact" onClick={() => setMobileOpen(false)} data-testid="mobile-menu-contact" className="col-span-2 flex items-center justify-center gap-2 px-3 h-10 rounded-lg bg-red-950/30 border border-red-700/30 text-red-200 text-[13px] font-semibold"><Mail size={14} /> Nous contacter</Link>
            </div>
            {user?.is_admin && (
              <Link to="/admin" onClick={() => setMobileOpen(false)} data-testid="mobile-menu-admin" className="flex items-center gap-2 px-3 h-10 rounded-lg bg-red-950/30 border border-red-700/30 text-red-300 text-[13px] mb-3"><Settings size={14} /> Back-office admin</Link>
            )}
            {user ? (
              <button onClick={onLogout} data-testid="mobile-menu-logout" className="w-full h-11 rounded-full bg-red-600 text-white text-[13.5px] font-semibold inline-flex items-center justify-center gap-2"><LogOut size={14} /> Se déconnecter</button>
            ) : (
              <div className="flex gap-2">
                <button onClick={() => { setMobileOpen(false); openLogin(); }} data-testid="mobile-menu-login" className="flex-1 h-11 rounded-full border border-white/10 text-white/90 text-[13.5px] font-medium">Connexion</button>
                <button onClick={() => { setMobileOpen(false); openRegister(); }} data-testid="mobile-menu-register" className="flex-1 h-11 rounded-full bg-red-600 text-white text-[13.5px] font-semibold">Créer un compte</button>
              </div>
            )}
            <p className="mt-3 text-[11px] text-white/40 text-center">Onglets principaux dans la barre du bas ↓</p>
          </div>
        )}
      </div>

      {/* Bandeau ONISR défilant — retiré de la page d'accueil (nuit au visuel), visible ailleurs sur desktop */}
      {showAlert && !hideBanners && !isHomePublic && (
        <div className="hidden md:block border-y border-red-900/40 bg-[#120a0b] overflow-hidden" data-testid="onisr-ticker">
          <div className="flex items-center h-9">
            <span className="shrink-0 flex items-center gap-1.5 px-3 h-full bg-red-950/60 border-r border-red-900/40 text-red-300 text-[10px] uppercase tracking-[0.15em] font-bold whitespace-nowrap z-10">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> {announcement.badge}
            </span>
            <div className="flex-1 overflow-hidden relative h-full">
              <div className="absolute inset-y-0 flex items-center animate-marquee whitespace-nowrap">
                {[0, 1].map((k) => (
                  <span key={k} className="flex items-center text-[12.5px] text-white/85 pr-16">
                    <span className="text-red-300/80 font-mono-spec text-[11px] uppercase tracking-wider mr-3">{announcement.date}</span>
                    {announcement.text}
                    <Link to={announcement.href || '/magazine'} data-testid={k === 0 ? 'header-alert-cta' : undefined} className="ml-3 text-red-400 hover:text-red-300 font-semibold">{announcement.cta}</Link>
                  </span>
                ))}
              </div>
            </div>
            <button onClick={() => setShowAlert(false)} aria-label="Fermer" className="shrink-0 px-2.5 h-full text-white/40 hover:text-white bg-[#120a0b] z-10"><X size={14} /></button>
          </div>
        </div>
      )}
    </header>
  );
}
